import React from "react";
import { connect } from "react-redux";
import { Dropdown, Grid, Loader, Dimmer, Button } from "semantic-ui-react";
import DownloadButton from "@buttons/DownloadButton";
import {
  getSuppliersRaw,
  loadSupplier,
  updateSupplier,
} from "@actions/index";
import { getAssetTable, getAssetsDetailsDownload, supplierDownload } from "@api/egm";
import { setCreateAssetModal, showSuccessAlert } from "@actions/modals";
import AssetMainTable from "./AssetMainTable";
import BaseSettings from "../systemAdmin/BaseSettings";
import OptionsList from "../systemAdmin/OptionsList";
import AssetDetailView from "./AssetDetailView";
import { ASSET_STATUS_FILTER_DATA } from "@helpers/constant";
import { handleDownload } from "@helpers/downloads";

class SupplierView extends BaseSettings {
  constructor(props) {
    super(props);
    this.state = {
      ...this.state,
      operatorId: null,
      suppliers: [],
      loading: false,
      saving: false,
      selectedSupplier: null,
      selectedOptions: {
        main: [],
        ownership: [],
        operational: [],
      },
      selectedNode: null,
      grid: null,
      doQuestion: false,
      doDelete: false,
      questionText: "",
      changed: [],
      local: Intl.DateTimeFormat().resolvedOptions().locale,
      showDetail: false,
      assetDetailStatusOption: ASSET_STATUS_FILTER_DATA[0].value,
      assetDetailAssets: [],
    };
  }

  componentDidUpdate(prevProps) {
    const { createAssetModal } = this.props;
    const prev = prevProps.createAssetModal ?? {};
    if (
      prev.open &&
      !createAssetModal?.open &&
      createAssetModal?.lastCreatedType === "SP"
    ) {
      this.getSuppliers();
      this.props.setCreateAssetModal({ lastCreatedType: null });
    }
  }

  componentDidMount() {
    const { operators } = this.props;
    if (operators.length === 1 && operators[0].value !== -1) {
      this.setState({ operatorId: operators[0].value }, this.getSuppliers);
    }
  }

  onOperatorChange = (e, { value }) => {
    this.setState(
      {
        operatorId: value,
        selectedSupplier: null,
        assetDetailStatusOption: ASSET_STATUS_FILTER_DATA[0].value,
        assetDetailAssets: [],
      },
      () => {
        if (value) {
          this.getSuppliers();
        }
      },
    );
  };

  getAssetTableData = (supplierId, statusOption) => {
    const { operatorId } = this.state;

    return getAssetTable(
      operatorId,
      -1,
      -1,
      -1,
      -1,
      -1,
      statusOption,
      -1,
      0,
      "#",
      -1,
      -1,
      supplierId,
      true,
    );
  };

  handleAssetStatusOptionChange = (option) => {
    const { selectedSupplier } = this.state;
    if (!selectedSupplier) return;

    this.setState(
      { assetDetailStatusOption: option, loading: true },
      () => {
        this.getAssetTableData(selectedSupplier.supplierId, option)
          .then((tableData) => {
            this.setState({
              assetDetailAssets: tableData.assets || [],
              loading: false,
            });
          })
          .catch(() => this.setState({ loading: false }));
      },
    );
  };

  getSuppliers = () => {
    const { operatorId } = this.state;
    if (!operatorId) return;
    this.setState({ loading: true }, () => {
      getSuppliersRaw(operatorId)
        .then((data) => {
          this.setState({
            suppliers: data.suppliers || [],
            loading: false,
          });
        })
        .catch(() => this.setState({ loading: false }));
    });
  };

  handleNew = (selectedOptions, selectedNode, grid = null) => {
    this.setState({
      selectedOptions: selectedOptions.main
        ? selectedOptions
        : { main: selectedOptions, ownership: [], operational: [] },
      changed: [],
      selectedNode,
      grid,
    });
  };

  onSelectRow = (item) => {
    const defaultStatus = ASSET_STATUS_FILTER_DATA[0].value;
    this.setState({
      loading: true,
      showDetail: true,
      assetDetailStatusOption: defaultStatus,
    });
    Promise.all([
      loadSupplier(item.supplierId),
      this.getAssetTableData(item.supplierId, defaultStatus),
    ])
      .then(([response, tableData]) => {
        const { doQuestion, doDelete, questionText } = response;
        this.setState(
          {
            doQuestion,
            doDelete,
            questionText,
            selectedSupplier: item,
            assetDetailAssets: tableData.assets || [],
          },
          () => {
            this.handleNew(
              response.assetUI
                ? response.assetUI
                : {
                    main: response?.options || [],
                    operational: [],
                    ownership: [],
                  },
              item,
              response.grid,
            );
            this.setState({ loading: false });
          },
        );
      })
      .catch(() =>
        this.setState({ loading: false }),
      );
  };

  onNew = () => {
    const { operatorId } = this.state;
    if (!operatorId) return;
    this.props.setCreateAssetModal({
      open: true,
      type: "SP",
      operatorId,
      name: "New Supplier",
    });
  };

  onSave = () => {
    const { operatorId, selectedSupplier, selectedOptions } = this.state;
    const { showSuccessAlert } = this.props;
    if (!operatorId || !selectedSupplier) return;
    const supplierId = selectedSupplier ? selectedSupplier.supplierId : 0;
    const changed = this.getAll(selectedOptions);

    this.setState({ saving: true }, () => {
      updateSupplier(supplierId, operatorId, changed, () =>
        this.getSuppliers(),
      )
        .then(() => {
          this.setState({ saving: false }, () => {
            showSuccessAlert(
              supplierId
                ? "Supplier updated successfully."
                : "Supplier created successfully.",
            );
          });
        })
        .catch(() => this.setState({ saving: false }));
    });
  };

  handleDownload = () => {
    const { operatorId } = this.state;
    if (!operatorId) return;
    this.setState({ loading: true }, () => {
      supplierDownload(operatorId).then(
        (response) => {
          this.setState({ loading: false }, () =>
            handleDownload(response),
          );
        },
        () => this.setState({ loading: false }),
      );
    });
  };

  handleAssetDetailDownload = () => {
    const { operatorId, selectedSupplier, assetDetailStatusOption } =
      this.state;
    if (!operatorId || !selectedSupplier) return;

    this.setState({ loading: true }, () => {
      getAssetsDetailsDownload(
        operatorId,
        -1,
        -1,
        -1,
        -1,
        -1,
        assetDetailStatusOption,
        -1,
        0,
        "#",
        -1,
        -1,
        selectedSupplier.supplierId,
        true,
      ).then(
        (response) => {
          this.setState({ loading: false }, () => handleDownload(response));
        },
        () => this.setState({ loading: false }),
      );
    });
  };

  render() {
    const {
      suppliers,
      loading,
      saving,
      selectedSupplier,
      selectedOptions,
      changed,
      operatorId,
    } = this.state;

    const SUPPLIER_TABLE_COLUMNS = [
      { key: "code", label: "Code", accessor: "supplierCode" },
      { key: "name", label: "Name", accessor: "supplierName" },
      { key: "address1", label: "Address 1", accessor: "address1" },
      { key: "address2", label: "Address 2", accessor: "address2" },
      { key: "town", label: "Town/City", accessor: "town" },
      { key: "postCode", 
        label: this.state.local === "en-GB" ? "Postcode" : "Zip Code", 
        accessor: this.state.local === "en-GB" ? "postCode" : "postCode" },
      { key: "county", 
        label: this.state.local === "en-GB" ? "County" : "State", 
        accessor: this.state.local === "en-GB" ? "county" : "county" },
      { key: "contact", label: "Contact", accessor: "contact" },
      { key: "contactNumber", label: "Contact Number", accessor: "contactNumber" },
      { key: "contactEmail", label: "Contact Email", accessor: "contactEmail" },
      {
        key: "installed",
        label: "Installed",
        accessor: (row) => row.installedCount ?? "-",
      },
      {
        key: "notInstalled",
        label: "Not Installed",
        accessor: (row) => row.notInstalledCount ?? "-",
      },
      { key: "pendingScrapped", label: "Pending Scrapped", accessor: "pendingScrappedCount" },
      { key: "sold", label: "Sold", accessor: "soldCount" },
    ];

    const canSave =
      !!selectedSupplier &&
      this.isAllValid(
        selectedOptions || { main: [], ownership: [], operational: [] },
      ) &&
      changed.length > 0;

    return (
      <div className="flex-stretch gs-flex-container-vertical height-100">
        <Grid className="height-100 flex-stretch">
          <Grid.Row
            className={`flex-stretch ${this.state.showDetail ? "h-50" : "h-100"}`}
          >
            <Grid.Column width={12} className="height-100 flex">
              {loading && (
                <Dimmer active inverted>
                  <Loader />
                </Dimmer>
              )}
              <div className="mb-0_5">
                <Dropdown
                  placeholder="Select operator"
                  selection
                  options={this.props.operators}
                  value={operatorId || ""}
                  onChange={this.onOperatorChange}
                  disabled={!this.props.operators.length}
                />
                <Button
                  className="ml-0_5"
                  icon="expand"
                  basic={!this.state.showDetail}
                  color="grey"
                  secondary
                  onClick={() =>
                    this.setState({ showDetail: !this.state.showDetail })
                  }
                  size="mini"
                  disabled={!selectedSupplier}
                />
              </div>
              <div className="mb-0_5 flex-stretch">
                <AssetMainTable
                  columns={SUPPLIER_TABLE_COLUMNS}
                  data={suppliers}
                  rowKey="supplierId"
                  selectedRowId={selectedSupplier?.supplierId}
                  onSelectRow={this.onSelectRow}
                />
              </div>
            </Grid.Column>
            <Grid.Column width={4} className="height-100 flex">
              <div className="flex-start">
                <Button
                  color="green"
                  content="New"
                  onClick={this.onNew}
                  disabled={!operatorId}
                  loading={loading}
                />
                <Button
                  primary
                  content="Save"
                  onClick={this.onSave}
                  disabled={!canSave}
                  loading={saving}
                />
                <DownloadButton
                  disabled={!this.state.operatorId}
                  caption="Export"
                  onClick={this.handleDownload}
                  loading={loading}
                />
              </div>
              <div className="flex-stretch height-100 p-1 border-grey-1 flex">
                <OptionsList
                  options={this.mapChanged(
                    (selectedOptions && selectedOptions.main) || [],
                  )}
                  onChange={this.handleChangeValue}
                />
              </div>
              {loading && (
                <Dimmer active inverted>
                  <Loader />
                </Dimmer>
              )}
            </Grid.Column>
          </Grid.Row>
          {this.state.showDetail && (
            <Grid.Row className="flex-stretch h-50">
              <Grid.Column width={16}>
                <AssetDetailView
                  statusOption={this.state.assetDetailStatusOption}
                  assets={this.state.assetDetailAssets}
                  loading={this.state.loading}
                  operatorId={this.state.operatorId}
                  detailExportDisabled={!this.state.selectedSupplier}
                  onStatusOptionChange={this.handleAssetStatusOptionChange}
                  onDownloadClick={this.handleAssetDetailDownload}
                />
              </Grid.Column>
            </Grid.Row>
          )}
        </Grid>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  operators: state.operators?.filter((x) => x.value !== -1) ?? [],
  createAssetModal: state.createAssetModal,
});

export default connect(mapStateToProps, {
  setCreateAssetModal,
  showSuccessAlert,
})(SupplierView);
